import { AnalyticsGranularity, AnalyticsUserSortKey } from '../types/analytics.types';

export const ANALYTICS_API_ENDPOINTS = {
  OVERVIEW: '/analytics/overview',
  TIMELINE: '/analytics/timeline',
  USERS: '/analytics/users',
} as const;

export interface AnalyticsRangePreset {
  id: string;
  label: string;
  days: number;
  granularity: AnalyticsGranularity;
}

export const ANALYTICS_RANGE_PRESETS: readonly AnalyticsRangePreset[] = [
  { id: '7d', label: 'Last 7 days', days: 7, granularity: 'day' },
  { id: '30d', label: 'Last 30 days', days: 30, granularity: 'day' },
  { id: '90d', label: 'Last 90 days', days: 90, granularity: 'week' },
  { id: '180d', label: 'Last 6 months', days: 180, granularity: 'week' },
  { id: '365d', label: 'Last 12 months', days: 365, granularity: 'month' },
] as const;

export const DEFAULT_RANGE_PRESET_ID = '30d';
export const DEFAULT_GRANULARITY: AnalyticsGranularity = 'day';

export const ANALYTICS_GRANULARITY_OPTIONS: ReadonlyArray<{
  value: AnalyticsGranularity;
  label: string;
}> = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
];

export const ANALYTICS_USER_SORT_OPTIONS: ReadonlyArray<{
  value: AnalyticsUserSortKey;
  label: string;
}> = [
  { value: 'total', label: 'Most engagements' },
  { value: 'created', label: 'Most created' },
  { value: 'updated', label: 'Most updated' },
];

export const ANALYTICS_COLORS = {
  CREATED: '#0f766e',
  UPDATED: '#f59e0b',
  CREATED_FILL: 'rgba(15, 118, 110, 0.16)',
  UPDATED_FILL: 'rgba(245, 158, 11, 0.18)',
  GRID: '#e2e8f0',
  AXIS: '#94a3b8',
} as const;

export const ANALYTICS_MESSAGES = {
  LOAD_FAILED: 'Failed to load analytics',
  EMPTY_TIMELINE: 'No activity in the selected range',
  EMPTY_USERS: 'No contributors in the selected range',
} as const;

export const TIMELINE_CHART_HEIGHT = 280;
export const LEADERBOARD_LIMIT = 10;

export const TIMELINE_CHART_CONFIG = {
  WIDTH: 960,
  PADDING_TOP: 16,
  PADDING_RIGHT: 16,
  PADDING_BOTTOM: 32,
  PADDING_LEFT: 40,
  MAX_X_AXIS_LABELS: 12,
  Y_AXIS_TICK_COUNT: 4,
  BAR_WIDTH_MIN_PX: 4,
  BAR_WIDTH_MAX_PX: 28,
  BAR_WIDTH_RATIO: 0.7,
  BAR_CORNER_RADIUS: 2,
  BAR_OPACITY_DEFAULT: 0.92,
  BAR_OPACITY_HOVER: 1,
  GRID_STROKE_WIDTH: 1,
  HOVER_LINE_STROKE_WIDTH: 1,
  HOVER_LINE_OPACITY: 0.6,
  HOVER_LINE_DASH: '2 2',
  GRID_DASH_NON_BASELINE: '3 3',
  AXIS_LABEL_FONT_SIZE: 10,
  Y_LABEL_OFFSET_X: 6,
  Y_LABEL_OFFSET_Y: 3,
  X_LABEL_OFFSET_Y: 18,
  MIN_BAR_HEIGHT_PX: 1,
} as const;

export const NICE_CEIL_CONFIG = {
  SMALL_CEIL: 5,
  MEDIUM_CEIL: 10,
  STEPS: [1, 2, 5, 10] as const,
} as const;

export const CHART_STATE_EXTRA_HEIGHT_PX = 40;

export const USER_SORT_KEYS = {
  TOTAL: 'total',
  CREATED: 'created',
  UPDATED: 'updated',
} as const;

export const PERCENT_MAX = 100;
export const CONTRIBUTION_BAR_MIN_DENOMINATOR = 1;
export const LEADERBOARD_RANK_DIGITS = 2;
export const INITIAL_RANK_INDEX = 1;

export const GRANULARITY_LABELS: Record<AnalyticsGranularity, { singular: string; adjective: string }> = {
  day: { singular: 'day', adjective: 'Daily' },
  week: { singular: 'week', adjective: 'Weekly' },
  month: { singular: 'month', adjective: 'Monthly' },
};

